import React, { useEffect, useState } from 'react'
import LayoutHeader from '../../../../layout/LayoutHeader'
import { useNavigate } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import RedirectCard from '../../../../components/More/RedirectCard'
import { setErrorAlert, setSuccessAlert, setLoader } from '../../../../store/alert/alertSlice'
import { postDataAPI, getDataAPI } from '../../../../utils/fetchData'

// Sophisticated card components matching the analytics design
const ContentCard = ({ title, children, color = '#22C55E' }) => (
    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all">
        <div className="flex items-center gap-2 mb-4">
            <div 
                className="w-[8px] h-[8px] rounded-full"
                style={{ backgroundColor: color }}
            />
            <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                {title}
            </span>
        </div>
        {children}
    </div>
);

const FeatureCard = ({ title, description, onClick, disabled = false, color = '#5856D6' }) => (
    <div 
        onClick={!disabled ? onClick : undefined}
        className={`bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 transition-all ${
            disabled 
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:border-[rgba(88,86,214,0.5)] hover:bg-[rgba(88,86,214,0.05)] cursor-pointer'
        }`}
    >
        <div className="flex items-start justify-between">
            <div className="flex-1">
                <h3 className="text-white text-[14px] font-semibold mb-1">
                    {title}
                </h3>
                <p className="text-[rgba(255,255,255,0.6)] text-[11px] leading-tight">
                    {description}
                </p>
            </div>
            <div className="ml-3 text-[rgba(255,255,255,0.4)]">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
            </div>
        </div>
    </div>
);

const Gmail = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { user } = useSelector((state) => state.auth)
    
    const [loading, setLoading] = useState(true)
    const [gmailConnection, setGmailConnection] = useState(null)
    const [trackedEmails, setTrackedEmails] = useState([])
    const [isConnecting, setIsConnecting] = useState(false)
    const [activeTab, setActiveTab] = useState('overview')

    // Tab configuration
    const tabs = [
        { id: 'overview', label: 'Overview' },
        { id: 'features', label: 'Features' },
        { id: 'settings', label: 'Settings' }
    ]

    useEffect(() => {
        fetchGmailStatus()
    }, [])

    const fetchGmailStatus = async () => {
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            console.log('Fetching Gmail status with token:', token ? 'Token exists' : 'No token')
            
            const response = await getDataAPI('user/gmail/status', token)
            console.log('Gmail status response:', response)
            
            if (response.data.status === 200) {
                setGmailConnection(response.data.data.connection)
                setTrackedEmails(response.data.data.trackedEmails || [])
                console.log('Gmail status loaded successfully')
            }
        } catch (error) {
            console.error('Error fetching Gmail status:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleConnectGmail = async () => {
        try {
            setIsConnecting(true)
            dispatch(setLoader(true))
            
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            console.log('🔑 Token for Gmail auth:', token ? 'Token exists' : 'No token')
            
            const response = await getDataAPI('user/gmail/auth-url', token)
            console.log('📧 Gmail auth URL response:', response)
            
            if (response.data.status === 200) {
                console.log('✅ Redirecting to Google OAuth:', response.data.data.authUrl)
                // Redirect to Google OAuth
                window.location.href = response.data.data.authUrl
            } else {
                console.error('❌ Failed response:', response)
                dispatch(setErrorAlert('Failed to initiate Gmail connection'))
            }
        } catch (error) {
            console.error('💥 Error connecting Gmail:', error)
            dispatch(setErrorAlert('Error connecting to Gmail'))
        } finally {
            setIsConnecting(false)
            dispatch(setLoader(false))
        }
    }

    const handleDisconnectGmail = async () => {
        try {
            dispatch(setLoader(true))
            
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            const response = await postDataAPI('user/gmail/disconnect', {}, token)
            
            if (response.data.status === 200) {
                setGmailConnection(null)
                setTrackedEmails([])
                dispatch(setSuccessAlert('Gmail disconnected successfully'))
            } else {
                dispatch(setErrorAlert('Failed to disconnect Gmail'))
            }
        } catch (error) {
            console.error('Error disconnecting Gmail:', error)
            dispatch(setErrorAlert('Error disconnecting Gmail'))
        } finally {
            dispatch(setLoader(false))
        }
    }

    if (loading) {
        return (
            <LayoutHeader>
                <div className="w-[390px] flex flex-col justify-center items-center h-screen">
                    <div className="text-white">Loading Gmail settings...</div>
                </div>
            </LayoutHeader>
        )
    }

    return (
        <LayoutHeader>
            <div className="w-[390px] flex flex-col justify-start items-center relative z-[0]">
                {/* Header */}
                <div className="px-4 my-[50px] flex w-full justify-between">
                    <p className="text-white">Gmail Integration</p>
                    <div className='flex gap-[20px]'>
                        <p onClick={() => navigate('/more/setting')} className='text-white cursor-pointer'>Go back</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="w-full mb-6">
                    <div className="flex border-b border-[rgba(255,255,255,0.1)] overflow-x-auto scrollbar-hide px-4">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex-shrink-0 px-4 py-2 text-[12px] transition-colors relative ${
                                    activeTab === tab.id 
                                        ? 'text-white' 
                                        : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                                }`}
                            >
                                {tab.label}
                                {activeTab === tab.id && (
                                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#22C55E]"></div>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Help Text */}
                <div className="w-full px-4 mb-6">
                    <div className="text-[12px] text-[rgba(255,255,255,0.5)] leading-[1.5]">
                        <p>Connect your Gmail to enable AI-powered email analysis and music industry insights.</p>
                        <p>Track important contacts, discover opportunities, and get intelligent career guidance from your email data.</p>
                    </div>
                </div>

                <div className="w-full px-4 space-y-4">
                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                        <>
                            {/* Connection Status */}
                            {gmailConnection ? (
                                <ContentCard title="Connected Account" color="#22C55E">
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[12px] text-white font-medium">Email Address</span>
                                            <span className="text-[12px] text-[#22C55E]">{gmailConnection.email}</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-[12px] text-[rgba(255,255,255,0.6)]">Connected</span>
                                            <span className="text-[12px] text-[rgba(255,255,255,0.6)]">
                                                {new Date(gmailConnection.connectedAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-[12px] text-[rgba(255,255,255,0.6)]">Last Sync</span>
                                            <span className="text-[12px] text-[rgba(255,255,255,0.6)]">
                                                {new Date(gmailConnection.lastSync).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>
                                </ContentCard>
                            ) : (
                                <ContentCard title="Gmail Connection" color="#6B7280">
                                    <div className="text-center py-2">
                                        <p className="text-[rgba(255,255,255,0.6)] text-[12px] mb-4 leading-relaxed">
                                            Connect your Gmail to enable AI-powered email analysis, track important contacts, and discover music industry opportunities.
                                        </p>
                                        <button
                                            onClick={handleConnectGmail}
                                            disabled={isConnecting}
                                            className="w-full bg-[rgba(34,197,94,0.1)] border border-[rgba(34,197,94,0.3)] text-[#22C55E] text-[12px] py-2 rounded-md hover:bg-[rgba(34,197,94,0.2)] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                        >
                                            {isConnecting ? 'Connecting...' : 'Connect Gmail'}
                                        </button>
                                    </div>
                                </ContentCard>
                            )}

                            {/* Stats - Only show if connected */}
                            {gmailConnection && (
                                <div className="grid grid-cols-2 gap-3">
                                    <ContentCard title="Tracked Contacts" color="#3B82F6">
                                        <div className="text-center">
                                            <div className="text-white text-[24px] font-bold">{trackedEmails.length}</div>
                                            <div className="text-[rgba(255,255,255,0.5)] text-[10px]">Active contacts</div>
                                        </div>
                                    </ContentCard>
                                    
                                                                <ContentCard title="Last Sync" color="#10B981">
                                <div className="text-center">
                                    <div className="text-white text-[24px] font-bold">
                                        {gmailConnection.lastSync ? 
                                            Math.floor((Date.now() - new Date(gmailConnection.lastSync)) / (1000 * 60 * 60)) < 1 ? 
                                                'Just now' : 
                                                Math.floor((Date.now() - new Date(gmailConnection.lastSync)) / (1000 * 60 * 60)) + 'h'
                                            : 'Never'
                                        }
                                    </div>
                                    <div className="text-[rgba(255,255,255,0.5)] text-[10px]">
                                        {gmailConnection.lastSync ? 'Last sync' : 'No sync yet'}
                                    </div>
                                </div>
                            </ContentCard>
                                </div>
                            )}

                            {/* How to Use - Only show if connected */}
                            {gmailConnection && (
                                <ContentCard title="How to Use Gmail Integration" color="#3B82F6">
                                    <div className="space-y-3 text-[11px] text-[rgba(255,255,255,0.6)] leading-relaxed">
                                        <p>
                                            Your Gmail is now connected! You can search your emails directly through the main AI chat at the bottom of any page.
                                        </p>
                                        <div className="space-y-2">
                                            <p className="text-white font-medium">Try asking:</p>
                                            <ul className="space-y-1 ml-3">
                                                <li>• "Show me my booking emails from last week"</li>
                                                <li>• "Find collaboration requests in my inbox"</li>
                                                <li>• "Search for emails from venues"</li>
                                                <li>• "What show opportunities do I have?"</li>
                                            </ul>
                                        </div>
                                        <p>
                                            The AI will automatically search your Gmail and provide insights about your music career opportunities.
                                        </p>
                                    </div>
                                </ContentCard>
                            )}
                        </>
                    )}

                    {/* Features Tab */}
                    {activeTab === 'features' && gmailConnection && (
                        <div className="space-y-3">
                            <FeatureCard
                                title="Email Contact Tracking"
                                description="Track emails from specific contacts for better context and insights"
                                onClick={() => navigate('/more/setting/gmail/tracking')}
                            />
                            
                            <FeatureCard
                                title="Music Industry Insights"
                                description="AI analysis of music industry opportunities from your emails"
                                disabled={true}
                            />
                            
                            <FeatureCard
                                title="Email Automation"
                                description="Automated responses and email management features"
                                disabled={true}
                            />
                        </div>
                    )}

                    {/* Settings Tab */}
                    {activeTab === 'settings' && (
                        <>
                            {gmailConnection ? (
                                <div className="space-y-4">
                                    <ContentCard title="Connection Settings" color="#EF4444">
                                        <div className="space-y-3">
                                            <p className="text-[rgba(255,255,255,0.6)] text-[11px] leading-relaxed">
                                                Manage your Gmail connection and data preferences.
                                            </p>
                                            <button
                                                onClick={handleDisconnectGmail}
                                                className="w-full bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] text-[#EF4444] text-[12px] py-2 rounded-md hover:bg-[rgba(239,68,68,0.2)] transition-all"
                                            >
                                                Disconnect Gmail
                                            </button>
                                        </div>
                                    </ContentCard>

                                    <ContentCard title="About Gmail Integration" color="#8B5CF6">
                                        <p className="text-[rgba(255,255,255,0.6)] text-[11px] leading-relaxed">
                                            Connect your Gmail to let Dizee's AI analyze your music industry emails, 
                                            track important contacts, extract show opportunities, and provide intelligent 
                                            insights about your career communications.
                                        </p>
                                    </ContentCard>
                                </div>
                            ) : (
                                <ContentCard title="No Connection" color="#6B7280">
                                    <div className="text-center py-2">
                                        <p className="text-[rgba(255,255,255,0.6)] text-[12px] mb-4 leading-relaxed">
                                            Connect your Gmail account first to access settings and features.
                                        </p>
                                        <button
                                            onClick={() => setActiveTab('overview')}
                                            className="w-full bg-[rgba(34,197,94,0.1)] border border-[rgba(34,197,94,0.3)] text-[#22C55E] text-[12px] py-2 rounded-md hover:bg-[rgba(34,197,94,0.2)] transition-all"
                                        >
                                            Go to Overview
                                        </button>
                                    </div>
                                </ContentCard>
                            )}
                        </>
                    )}
                </div>
            </div>
        </LayoutHeader>
    )
}

export default Gmail 